/* Home Artists Grid — fetches top-N sample artworks per artist via the
   accounts app proxy in one Vespa grouping query (no per-artist Shopify
   collection required) and populates each card's work strip. Each thumb
   links to its product page; the trailing "View all" links to the artist. */

(() => {
  const ENDPOINT = '/a/accounts/artists-with-samples';
  const PER_ARTIST = 5;

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function escapeAttr(s) {
    return escapeHtml(s);
  }

  function normalize(s) {
    return String(s || '').trim().toLowerCase();
  }

  function getLocalePrefix() {
    const m = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);
    return m ? `/${m[1]}` : '';
  }

  function productUrl(handle, localePrefix) {
    if (!handle) return '#';
    return `${localePrefix}/products/${encodeURIComponent(handle)}`;
  }

  function renderWorks(samples, artistUrl, viewLabel, localePrefix) {
    const thumbs = samples.slice(0, PER_ARTIST).map((s) => {
      const href = productUrl(s.handle, localePrefix);
      const img = s.imageUrl
        ? `<img src="${escapeAttr(s.imageUrl)}" alt="${escapeAttr(s.title || '')}" class="home-artists-grid__work-img" loading="lazy" decoding="async">`
        : '';
      return `<a class="home-artists-grid__work" href="${escapeAttr(href)}" title="${escapeAttr(s.title || '')}">${img}</a>`;
    }).join('');
    const view = artistUrl
      ? `<a class="home-artists-grid__view" href="${escapeAttr(artistUrl)}">${escapeHtml(viewLabel || 'View all')} →</a>`
      : '';
    return thumbs + view;
  }

  async function fetchArtists(names) {
    const url = `${ENDPOINT}?artists=${encodeURIComponent(names.join(','))}&per_artist=${PER_ARTIST}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async function hydrate(section) {
    const cards = Array.from(section.querySelectorAll('.home-artists-grid__card[data-artist-name]'));
    if (!cards.length) return;

    const names = cards.map((c) => c.dataset.artistName).filter(Boolean);
    const uniqueNames = Array.from(new Set(names));
    if (!uniqueNames.length) return;

    let data;
    try {
      data = await fetchArtists(uniqueNames);
    } catch (e) {
      console.warn('[home-artists-grid] fetch failed:', e);
      cards.forEach((c) => {
        const slot = c.querySelector('[data-artist-works-slot]');
        if (slot) slot.remove();
      });
      return;
    }

    const localePrefix = getLocalePrefix();
    const byName = new Map();
    (data.artists || []).forEach((a) => byName.set(normalize(a.value), a));

    cards.forEach((card) => {
      const slot = card.querySelector('[data-artist-works-slot]');
      if (!slot) return;
      const match = byName.get(normalize(card.dataset.artistName));
      if (!match || !match.samples || !match.samples.length) {
        slot.remove();
        return;
      }
      slot.innerHTML = renderWorks(
        match.samples,
        slot.dataset.artistUrl,
        slot.dataset.viewLabel,
        localePrefix
      );
      slot.classList.remove('home-artists-grid__works--loading');
      slot.removeAttribute('aria-hidden');
    });
  }

  function init() {
    const section = document.querySelector('.home-artists-grid');
    if (!section) return;

    if (!('IntersectionObserver' in window)) {
      hydrate(section);
      return;
    }

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          io.disconnect();
          hydrate(section);
          break;
        }
      }
    }, { rootMargin: '400px' });
    io.observe(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
